import React, { useEffect, useCallback, useRef } from 'react';

type VariantModalProps = {
  open: boolean;
  title?: string;
  onClose: () => void;
  onRequestClose?: (reason: 'backdrop' | 'esc' | 'close-button') => boolean | Promise<boolean>;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  disableBackdropClose?: boolean;
  disableEscClose?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  header?: React.ReactNode;
  hideCloseButton?: boolean;
  'data-testid'?: string;
};

// Floating Card styled modal with soft gradient backdrop and rounded corners.
function FloatingModal({
  open,
  title,
  onClose,
  onRequestClose,
  children,
  footer,
  className = '',
  disableBackdropClose = false,
  disableEscClose = false,
  closeOnBackdrop,
  closeOnEsc,
  header,
  hideCloseButton = false,
  'data-testid': testId,
}: VariantModalProps) {
  const allowBackdrop = (closeOnBackdrop ?? true) && !disableBackdropClose;
  const allowEsc = (closeOnEsc ?? true) && !disableEscClose;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleRequestClose = useCallback((reason: 'backdrop' | 'esc' | 'close-button') => {
    if ((reason === 'backdrop' && !allowBackdrop) || (reason === 'esc' && !allowEsc)) {
      return;
    }
    if (typeof onRequestClose === 'function') {
      const result = onRequestClose(reason);
      if (result !== null && typeof result === 'object' && typeof (result as any).then === 'function') {
        void (result).then((v) => { if (v !== false) onClose(); });
        return;
      }
      if (result === false) return;
    }
    onClose();
  }, [allowBackdrop, allowEsc, onRequestClose, onClose]);

  useEffect(() => {
    if (!open || !allowEsc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleRequestClose('esc'); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, allowEsc, handleRequestClose]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gradient-to-br from-blueberry-50/60 to-blueberry-100/60 backdrop-blur-sm"
        onClick={allowBackdrop ? () => handleRequestClose('backdrop') : undefined}
      />
      <div
        role="dialog"
        aria-modal="true"
        ref={containerRef}
        className={`relative bg-bg-raised text-content-primary rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.12)] max-w-6xl w-[min(100%,67rem)] mx-4 overflow-hidden transform transition-all duration-300 ease-out ${className} flex flex-col md:max-h-[calc(100vh-96px)]`}
        data-testid={testId}
      >
        <div className="px-4 py-3 border-b border-border-tertiary flex items-center justify-between flex-none">
          {header ? (
            header
          ) : (
            <>
              <h3 className="text-xl font-semibold text-content-primary">{title}</h3>
              {!hideCloseButton && (
                <button
                  onClick={() => handleRequestClose('close-button')}
                  className="text-icon-subtle hover:text-content-tertiary hover:bg-bg-tertiary rounded-lg p-1.5 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>
        <div data-modal-body-scroll className="px-4 py-3 flex-1 overflow-auto">
          {children}
        </div>
        {footer && (
          <div className="px-4 py-3 border-t border-border-tertiary bg-bg-secondary flex items-center justify-end gap-3 flex-none">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default FloatingModal;

// Modern card-styled modal with dark backdrop - cleaner, more focused design
// Supports inline mode for embedding modal appearance without overlay (e.g., previews)
export function CardModal({
  open,
  title,
  subtitle,
  onClose,
  onRequestClose,
  children,
  footer,
  className = '',
  disableBackdropClose = false,
  disableEscClose = false,
  closeOnBackdrop,
  closeOnEsc,
  hideCloseButton = false,
  inline = false,
  'data-testid': testId,
}: VariantModalProps & { subtitle?: string; inline?: boolean }) {
  const allowBackdrop = (closeOnBackdrop ?? true) && !disableBackdropClose;
  const allowEsc = (closeOnEsc ?? true) && !disableEscClose;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleRequestClose = useCallback((reason: 'backdrop' | 'esc' | 'close-button') => {
    if ((reason === 'backdrop' && !allowBackdrop) || (reason === 'esc' && !allowEsc)) {
      return;
    }
    if (typeof onRequestClose === 'function') {
      const result = onRequestClose(reason);
      // Support both sync and async onRequestClose
      if (result !== null && typeof result === 'object' && typeof (result as any).then === 'function') {
        void (result).then((v) => { if (v !== false) onClose(); });
        return;
      }
      if (result === false) return;
    }
    onClose();
  }, [allowBackdrop, allowEsc, onRequestClose, onClose]);

  useEffect(() => {
    if (!open || !allowEsc || inline) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleRequestClose('esc'); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, allowEsc, inline, handleRequestClose]);

  useEffect(() => {
    if (!open || inline) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open, inline]);

  if (!open) return null;

  // Card content - shared between inline and overlay modes
  const cardContent = (
    <div
      role={inline ? undefined : 'dialog'}
      aria-modal={inline ? undefined : true}
      ref={containerRef}
      className={`bg-bg-raised text-content-primary rounded-2xl shadow-xl w-full overflow-hidden flex flex-col ${inline ? '' : 'relative max-w-5xl max-h-[calc(100vh-2rem)]'} ${className}`}
      data-testid={testId}
    >
      {/* Header - gradient blue like bundle modal */}
      <div className="bg-gradient-to-r from-blueberry-500 to-blueberry-600 px-6 py-4 flex items-start justify-between flex-none">
        <div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {subtitle && (
            <p className="text-sm text-blueberry-100 mt-0.5">{subtitle}</p>
          )}
        </div>
        {!hideCloseButton && !inline && (
          <button
            onClick={() => handleRequestClose('close-button')}
            className="rounded-lg p-2 hover:bg-white/10 transition-colors -mr-1.5 -mt-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {/* Body */}
      <div data-modal-body-scroll className="px-6 py-4 flex-1 overflow-auto">
        {children}
      </div>
      {/* Footer */}
      {footer && (
        <div className="px-6 py-3 border-t border-border-secondary bg-bg-secondary flex items-center justify-end gap-3 flex-none">
          {footer}
        </div>
      )}
    </div>
  );

  // Inline mode: just render the card without overlay
  if (inline) {
    return cardContent;
  }

  // Overlay mode: full modal with backdrop
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Dark semi-transparent backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={allowBackdrop ? () => handleRequestClose('backdrop') : undefined}
      />
      {cardContent}
    </div>
  );
}
